/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useMemo, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'I18n';
import getLang from 'I18n/utils/getLang';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { OLAF } from 'HubStyleTokens/colors';
import UILink from 'UIComponents/link/UILink';
import UIButton from 'UIComponents/button/UIButton';
import UIFlex from 'UIComponents/layout/UIFlex';
import { H5 } from 'UIComponents/elements/headings';
import CoreStorefrontHeader from 'marketplace-ui-common/storefront/components/StorefrontHeader';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import InputHighlighter from 'marketplace-ui-common/search/components/InputHighlighter';
import { TYPEAHEAD_SEARCH_MIN_CHARS } from 'marketplace-ui-common/constants/search';
import * as Industries from 'marketplace-ui-solutions-core/constants/routes/IndustryRoute';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { useScreenSize } from 'marketplace-ui-common/hooks/useScreenSize';
import { useProfileSearchBarQuery } from 'marketplace-ui-solutions-core/search/hooks/useProfileSearchBarQuery';
import { buildGetCachedSearchValues, buildSetCachedSearchValue } from 'marketplace-ui-common/search/utils';
import { debounce } from 'ecosystem-marketplace-core/utils/debounce';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import SearchBar from 'marketplace-ui-common/search/components/SearchBar';
import { updateStorefrontFilters, storefrontFiltersInitialState, getMultiSelectStorefrontFilters } from '../slices/storefrontFiltersSlice';
import * as FilterKey from '../constants/search/FilterKey';
import { getSortOptionValue } from '../utils/filters';
import { RELEVANCE_DESC } from '../constants/search/SortField';
import StorefrontUsageTrackerContext from '../context/StorefrontUsageTrackerContext';
import { getCanonicalAppContext, getIsPublicApp } from '../selectors/app';
import { push } from '../utils/history';
import { translateFilterValue } from '../utils/i18n';
import { getFullLocationUrl } from '../utils/urls';
import MobileStorefrontHeader from './MobileStorefrontHeader';
import { getServices } from '../slices/serviceCatalogSlice';
import HeroSection from './homepage/HeroSection';
import FindPartnerWizard from './homepage/questionnaire/FindPartnerWizard';
import { clearRecentActivity } from '../utils/homepage';
const CACHED_SEARCH_VALUES_KEY = 'ecosystem-marketplace-solutions:searches';
const getCachedSearchValues = buildGetCachedSearchValues(CACHED_SEARCH_VALUES_KEY);
const setCachedSearchValue = buildSetCachedSearchValue(CACHED_SEARCH_VALUES_KEY);

const getHeaderCustomStyle = isMobile => {
  return {
    MAIN_SECTION_CLASSNAME: isMobile ? 'p-top-7' : 'p-top-14',
    BACKGROUND_COLOR: '#00497D',
    GRADIENT_ANGLE: '193.97deg',
    START_GRADIENT_COLOR: '#00497D',
    END_GRADIENT_COLOR: '#0FBFBF'
  };
};

const getProfileOptionsGroup = (items, input) => {
  const filteredOptions = items.map(result => result.item ? {
    dropdownText: result.dropdownText,
    slug: result.item.slug,
    text: result.text,
    title: result.text,
    value: result.item.id
  } : {
    dropdownText: /*#__PURE__*/_jsx(UILink, {
      children: result.text
    }),
    text: result.text,
    value: input,
    viewAll: true
  });
  return {
    text: I18n.text(`ecosystem-marketplace-solutions-ui.storefront.header.search.typeaheadResultsHeader`),
    options: filteredOptions
  };
};

const getIndustryOptionsGroup = input => {
  const filteredOptions = Object.keys(Industries).filter(industry => translateFilterValue(FilterKey.INDUSTRY, industry).toLowerCase().includes(input)).map(industry => {
    const translatedText = translateFilterValue(FilterKey.INDUSTRY, industry);
    return {
      dropdownText: /*#__PURE__*/_jsx(InputHighlighter, {
        text: translatedText,
        value: input
      }),
      filterKey: FilterKey.INDUSTRY,
      text: translatedText,
      value: industry
    };
  });
  return {
    text: I18n.text(`ecosystem-marketplace-solutions-ui.storefront.filters.industry.label`),
    options: filteredOptions
  };
};

const StorefrontHeader = () => {
  const dispatch = useDispatch();
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const canonicalAppContext = useSelector(getCanonicalAppContext);
  const filters = useSelector(getMultiSelectStorefrontFilters);
  const catalogServices = useSelector(getServices);
  const {
    browseAllPartnersMarginAdjustment,
    placeholder
  } = useResponsiveProps({
    placeholder: {
      xs: undefined,
      sm: I18n.text(`ecosystem-marketplace-solutions-ui.storefront.header.search.placeholder`)
    },
    browseAllPartnersMarginAdjustment: {
      xl: '-30px',
      lg: '0px'
    }
  });
  const [showFindPartnerWizard, setShowFindPartnerWizard] = useState(false);
  const cachedSearchValues = getCachedSearchValues();
  const recentSearchOptions = useMemo(() => {
    if (!cachedSearchValues.length) {
      return [];
    }

    return [{
      text: I18n.text(`ecosystem-marketplace-solutions-ui.storefront.header.search.recent`),
      options: cachedSearchValues.map(value => ({
        text: value,
        value,
        cachedSearchValue: true
      }))
    }];
  }, [cachedSearchValues]);
  const isOnHomepage = useMemo(() => {
    const homepageRegExp = new RegExp(/marketplace\/solutions(\/)?$/);
    return homepageRegExp.test(window.location.pathname);
  }, [window.location.pathname]);
  const getServiceOptionsGroup = useCallback(input => {
    const filteredOptions = catalogServices.filter(service => service.name.toLowerCase().includes(input)).map(service => {
      return {
        dropdownText: /*#__PURE__*/_jsx(InputHighlighter, {
          text: service.name,
          value: input
        }),
        filterKey: FilterKey.SERVICE,
        text: service.name,
        value: service.id
      };
    });
    return {
      text: I18n.text(`ecosystem-marketplace-solutions-ui.storefront.filters.service.label`),
      options: filteredOptions
    };
  }, [catalogServices]);
  const onSearchSubmit = useCallback(value => {
    const valuesFromCache = getCachedSearchValues();
    setCachedSearchValue(value, valuesFromCache);
    dispatch(updateStorefrontFilters({
      updates: Object.assign({}, storefrontFiltersInitialState, {
        [FilterKey.SEARCH]: value,
        [FilterKey.SORTS]: RELEVANCE_DESC
      })
    }));
    trackInteraction('submit search', {
      search: value,
      searchLength: value.length
    });
  }, [dispatch, trackInteraction]);
  const onClearSearch = useCallback(() => {
    if (filters[FilterKey.SEARCH]) {
      dispatch(updateStorefrontFilters({
        updates: {
          [FilterKey.SEARCH]: storefrontFiltersInitialState[FilterKey.SEARCH]
        }
      }));
      dispatch(updateStorefrontFilters({
        updates: {
          [FilterKey.SORTS]: storefrontFiltersInitialState[FilterKey.SORTS]
        }
      }));
    }
  }, [dispatch, filters]);
  const trackSelectedOptionInteraction = useCallback((itemType, value) => {
    trackInteraction('select search autofill item', {
      itemType,
      value
    });
  }, [trackInteraction]);
  const onSelectedOptionChange = useCallback(({
    target: {
      value: option
    }
  }) => {
    const {
      value,
      filterKey,
      cachedSearchValue,
      slug,
      viewAll
    } = option;

    if (viewAll) {
      onSearchSubmit(value);
      trackSelectedOptionInteraction('see all results', value);
      return;
    }

    if (filterKey) {
      dispatch(updateStorefrontFilters({
        updates: {
          [filterKey]: [value]
        }
      }));
      trackSelectedOptionInteraction(filterKey, value);
      return;
    }

    if (cachedSearchValue) {
      onSearchSubmit(value);
      trackSelectedOptionInteraction('recent search', value);
      return;
    }

    push(`/${slug}`);
    trackSelectedOptionInteraction('profile', slug);
  }, [dispatch, onSearchSubmit, trackSelectedOptionInteraction]);
  const isPublicApp = useSelector(getIsPublicApp);
  const {
    fetcher
  } = useProfileSearchBarQuery({
    appContext: isPublicApp ? 'public' : 'app',
    query: {
      sorts: getSortOptionValue(RELEVANCE_DESC),
      callingUserLanguage: getLang()
    }
  });

  const buildOptionsFromRecords = (recordData, input) => {
    const profileOptions = getProfileOptionsGroup(recordData, input);
    const serviceOptions = getServiceOptionsGroup(input);
    const industryOptions = getIndustryOptionsGroup(input);
    return [serviceOptions, profileOptions, industryOptions];
  };

  const loadOptions = useCallback(debounce((input, callback) => {
    if (input.trim().length < TYPEAHEAD_SEARCH_MIN_CHARS) {
      callback(null, {
        options: recentSearchOptions
      });
      return;
    }

    fetcher(input).then(resp => {
      const options = buildOptionsFromRecords(resp, input);
      callback(null, {
        options
      });
    }).catch(() => {
      Raven.captureMessage('Profile typeahead search failed', {
        level: 'error',
        extra: {
          search: input
        }
      });
      const options = buildOptionsFromRecords([], input);
      callback(null, {
        options
      });
    });
  }), [filters, recentSearchOptions]);
  const screenSize = useScreenSize();
  const isMobileView = screenSize === ScreenSizes.XS || screenSize === ScreenSizes.SM;
  const headerCustomStyle = useMemo(() => getHeaderCustomStyle(isMobileView), [isMobileView]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(CoreStorefrontHeader, {
      collapsedTitle: /*#__PURE__*/_jsx(UILink, {
        use: "unstyled",
        href: getFullLocationUrl({
          pathname: '/'
        }, canonicalAppContext),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `ecosystem-marketplace-solutions-ui.storefront.header.collapsedTitle`
        })
      }),
      customStyle: headerCustomStyle,
      use: "collapsed",
      children: /*#__PURE__*/_jsx(MediaQuery, {
        minScreenSize: ScreenSizes.SM,
        alternate: /*#__PURE__*/_jsx(MobileStorefrontHeader, {
          customStyle: headerCustomStyle,
          onClearSearch: onClearSearch,
          onSearchSubmit: onSearchSubmit,
          loadOptions: loadOptions,
          onSelectedOptionChange: onSelectedOptionChange,
          placeholder: placeholder,
          searchTerm: filters[FilterKey.SEARCH]
        }),
        children: /*#__PURE__*/_jsxs(UIFlex, {
          justify: "between",
          children: [/*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: isOnHomepage ? ScreenSizes.SM : ScreenSizes.LG,
            children: /*#__PURE__*/_jsx(UIButton, {
              style: {
                marginLeft: browseAllPartnersMarginAdjustment
              },
              onClick: () => {
                trackInteraction('click browse all partners');
                push('/all');
              },
              use: "unstyled",
              truncate: true,
              className: "p-top-3 m-right-4",
              children: /*#__PURE__*/_jsx(H5, {
                style: {
                  color: OLAF
                },
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `ecosystem-marketplace-solutions-ui.storefront.header.browseAll`
                })
              })
            })
          }), /*#__PURE__*/_jsxs(UIFlex, {
            justify: "end",
            children: [!isOnHomepage && /*#__PURE__*/_jsx(MediaQuery, {
              minScreenSize: ScreenSizes.MD,
              children: /*#__PURE__*/_jsx(UIButton, {
                className: "m-right-2",
                use: "secondary-ghost",
                onClick: () => {
                  trackInteraction('click find a partner');
                  clearRecentActivity();
                  setShowFindPartnerWizard(true);
                },
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `ecosystem-marketplace-solutions-ui.storefront.header.findPartner`
                })
              })
            }), /*#__PURE__*/_jsx("div", {
              style: {
                flexGrow: 'inherit',
                maxWidth: 400
              },
              children: /*#__PURE__*/_jsx(SearchBar, {
                loadOptions: loadOptions,
                inputSize: 'default',
                inputValue: filters[FilterKey.SEARCH],
                onClear: onClearSearch,
                onSearchSubmit: onSearchSubmit,
                onSelectedOptionChange: onSelectedOptionChange,
                placeholder: placeholder,
                menuStyle: {
                  height: 'auto',
                  maxHeight: '400px'
                }
              })
            })]
          })]
        })
      })
    }), isOnHomepage && /*#__PURE__*/_jsx(HeroSection, {
      onShowFindPartnerWizard: () => {
        trackInteraction('click find a partner');
        clearRecentActivity();
        setShowFindPartnerWizard(true);
      },
      isMobileView: isMobileView
    }), showFindPartnerWizard && /*#__PURE__*/_jsx(FindPartnerWizard, {
      onClose: () => setShowFindPartnerWizard(false),
      isMobileView: isMobileView
    })]
  });
};

export default StorefrontHeader;